/**
 * ReservoirLayoutPage.tsx — Barcombe Reservoir Bathymetric Layout
 *
 * Survey: Shoreline Surveys Ltd, Project J2020, Drawing 01v2, January 2023
 * Client: Land & Water Services Limited | Operator: South East Water
 * Scale 1:1000 on A1 | Datum: ODN Newlyn | Water level: 8.79 m ODN
 * Grid: OSGB36 OSTN15 | Easting ~543,750–544,000 E | Northing ~115,000 N
 * Depth range: 0.79 m (shoreline) → 4.79 m (NE basin)
 * Depth contours at 0.25 m intervals
 *
 * Depth conversion: depth_below_surface = 8.79 − ODN_elevation
 *
 * Module design parameters are read from sessionStorage (rb_diffuser_module_v1).
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  MapPin, Layers, ArrowLeft, Info, CheckCircle2,
  AlertTriangle, BarChart3, BookOpen, Settings, Activity, Wind,
  ClipboardCheck, Sparkles, Target, Zap, TrendingUp, Ruler,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS } from '@/lib/sessionPersistence';
import { generateReservoirLayoutPDF } from '@/lib/reservoirPdf';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import { toast } from 'sonner';

// ─── Session key ──────────────────────────────────────────────────────────────
const MODULE_KEY = 'rb_diffuser_module_v1';

// ─── Survey constants ─────────────────────────────────────────────────────────
const WATER_LEVEL_ODN = 8.79;   // m ODN

// ─── Physics constants ────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const GAMMA_AIR    = 1.4;
const FRAME_SIZE   = 2.0;

// ─── Physics ──────────────────────────────────────────────────────────────────
function rho_w(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
function mu_w(T: number)    { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function nu_w(T: number)    { return mu_w(T) / rho_w(T); }
function sigma_w(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number)   { return 468 / (31.6 + T); }

function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p  = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * sigma_w(T) * d_p) / (rho_w(T) * G), 1/3);
  const q_p  = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}
function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(nu_w(20) / nu_w(T), 0.7);
}
function sotePerM(d_mm: number) {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm)));
}
// Type-aware: returns pores PER UNIT (per disc OR per tube)
function diffuserPores(diffType: string, diamMm: number, m?: { porePerCm?: number; tubeLengthM?: number }): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube') {
    const porePerCm = m?.porePerCm ?? 8;
    const tubeLenM  = m?.tubeLengthM ?? 1.8;
    return Math.max(1, Math.round(porePerCm * tubeLenM * 100));
  }
  const r = diamMm / 2;
  if (diffType === 'membrane_disc_ptfe') return Math.round(0.40 * Math.PI * r * r);
  if (diffType === 'ceramic_dome')       return Math.round(0.10 * Math.PI * r * r);
  return Math.round(0.35 * Math.PI * r * r);
}
function unitsPerModule(diffType: string, m: { diffuserCount: number; tubeCount?: number }): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube')
    return Math.max(1, m.tubeCount ?? 2);
  return m.diffuserCount;
}
function blowerKw(nm3h: number, submM: number, dropKpa: number, safetyPct: number, eff: number) {
  const Q = nm3h / 3600;
  const P_out = P_ATM + (rho_w(20) * G * submM + dropKpa * 1000) * (1 + safetyPct / 100);
  const exp = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}

// ─── Reservoir polygon ────────────────────────────────────────────────────────
// Approximate boundary digitised from J2020 survey (Shoreline Surveys Ltd, Jan 2023).
// SVG coordinate system: 1 unit = 1 metre, Y increases southward.
// Reservoir bounding box: x[34,170] × y[14,432]. Clockwise from NW.
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415],
  [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

// ─── Depth zone ellipses ──────────────────────────────────────────────────────
// Approximate zones traced from J2020 survey colour key.
// depth = WATER_LEVEL_ODN − ODN_elevation
interface ZoneEllipse {
  cx: number; cy: number; rx: number; ry: number;
  depthMin: number; depthMax: number; depthMid: number;
  color: string; svgColor: string; label: string;
}
const DEPTH_ZONE_ELLIPSES: ZoneEllipse[] = [
  // Medium-deep (3.0–4.0 m) — teal-green patches
  { cx: 92,  cy: 88,  rx: 38, ry: 52, depthMin: 3.0, depthMax: 4.0, depthMid: 3.5, color: '#3d8b6b', svgColor: '#2a6b4e', label: '3.0–4.0 m' },
  { cx: 62,  cy: 195, rx: 20, ry: 35, depthMin: 3.0, depthMax: 4.0, depthMid: 3.3, color: '#3d8b6b', svgColor: '#2a6b4e', label: '3.0–4.0 m' },
  { cx: 100, cy: 278, rx: 22, ry: 28, depthMin: 3.0, depthMax: 4.0, depthMid: 3.2, color: '#3d8b6b', svgColor: '#2a6b4e', label: '3.0–4.0 m' },
  { cx: 152, cy: 170, rx: 14, ry: 22, depthMin: 3.0, depthMax: 4.0, depthMid: 3.1, color: '#3d8b6b', svgColor: '#2a6b4e', label: '3.0–4.0 m' },
  // Deepest (4.0–4.8 m) — blue (NE basin)
  { cx: 150, cy: 90,  rx: 19, ry: 26, depthMin: 4.0, depthMax: 4.8, depthMid: 4.4, color: '#4466a8', svgColor: '#2a4488', label: '4.0–4.8 m' },
];

// ─── Geometry utilities ───────────────────────────────────────────────────────
function inPolygon(px: number, py: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}
function inEllipse(px: number, py: number, z: ZoneEllipse): boolean {
  return ((px - z.cx) / z.rx) ** 2 + ((py - z.cy) / z.ry) ** 2 <= 1;
}
function segDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1, lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - x1 - t * dx, py - y1 - t * dy);
}
function edgeDist(px: number, py: number): number {
  let d = Infinity;
  for (let i = 0, j = RESERVOIR_POLY.length - 1; i < RESERVOIR_POLY.length; j = i++) {
    const s = segDist(px, py, RESERVOIR_POLY[j][0], RESERVOIR_POLY[j][1], RESERVOIR_POLY[i][0], RESERVOIR_POLY[i][1]);
    if (s < d) d = s;
  }
  return d;
}
function getDepthAt(px: number, py: number): number {
  if (!inPolygon(px, py, RESERVOIR_POLY)) return 0;
  for (const z of [...DEPTH_ZONE_ELLIPSES].sort((a, b) => b.depthMid - a.depthMid))
    if (inEllipse(px, py, z)) return z.depthMid;
  const d = edgeDist(px, py);
  if (d < 8)  return 0.9;
  if (d < 16) return 1.5;
  return 2.4;
}
function polygonAreaM2(poly: [number, number][]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  return Math.abs(a / 2);
}

// ─── Module placement ─────────────────────────────────────────────────────────
interface ModulePoint { x: number; y: number; depth: number; zoneId?: string }

// 8 array zone centres (matching all other pages and PDF generators)
// Modules are distributed across these — NOT a grid scan.
const ARRAY_ZONE_CENTRES = [
  { id: 'A', label: 'Deepest basin (NE)',        cx: 150, cy: 90,  depth: 4.4 },
  { id: 'B', label: 'NW-central deep patch',     cx: 92,  cy: 88,  depth: 3.5 },
  { id: 'C', label: 'Central-west deep',          cx: 62,  cy: 195, depth: 3.3 },
  { id: 'D', label: 'SE central deep',            cx: 100, cy: 278, depth: 3.2 },
  { id: 'E', label: 'East-central deep',          cx: 152, cy: 170, depth: 3.1 },
  { id: 'F', label: 'Silt zone - north',           cx: 105, cy: 135, depth: 2.5 },
  { id: 'G', label: 'Silt zone - middle',          cx: 115, cy: 230, depth: 2.4 },
  { id: 'H', label: 'Silt zone - south',           cx: 92,  cy: 340, depth: 2.3 },
];

/**
 * Distribute the recommended module count across the 8 named array zones in
 * a small hex pattern centred on each zone. NOT a uniform grid - reflects
 * actual deployment geometry (matches PDF/master report output exactly).
 */
function placeModulesInZones(totalCount: number, minDepthM: number): ModulePoint[] {
  const validZones = ARRAY_ZONE_CENTRES.filter(z => z.depth >= minDepthM);
  if (validZones.length === 0) return [];
  const mods: ModulePoint[] = [];
  // Weight by depth bonus - deeper zones get a few more modules
  const weights = validZones.map(z => 1 + 0.3 * (z.depth - 2.0));
  const sumW = weights.reduce((s, w) => s + w, 0);
  let placed = 0;
  for (let zi = 0; zi < validZones.length; zi++) {
    const z = validZones[zi];
    const portion = Math.max(1, Math.round(totalCount * weights[zi] / sumW));
    // Hex-pattern around centre
    const cols = Math.ceil(Math.sqrt(portion));
    for (let i = 0; i < portion && placed < totalCount; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const offsetX = (row % 2) * 3;
      mods.push({
        x: z.cx + (col - cols / 2) * 6 + offsetX,
        y: z.cy + (row - cols / 2) * 5,
        depth: z.depth,
        zoneId: z.id,
      });
      placed++;
    }
  }
  return mods;
}

// Backward-compat: alias old name to new function so existing call signature
// still works (now spacingM is ignored, we distribute recommended count)
function placeModules(spacingM: number, minDepthM: number, totalCount?: number): ModulePoint[] {
  void spacingM; // legacy parameter, no longer used
  // Default fallback if totalCount not supplied — use a reasonable AWWA value
  return placeModulesInZones(totalCount ?? 16, minDepthM);
}

// Grid coverage (sample points, check if within radius of any module)
function calcCoverage(mods: ModulePoint[], rM: number): number {
  const STEP = 4;
  let total = 0, covered = 0;
  for (let y = 18; y < 440; y += STEP) {
    for (let x = 34; x < 175; x += STEP) {
      if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
      total++;
      if (mods.some(m => Math.hypot(x - m.x, y - m.y) <= rM)) covered++;
    }
  }
  return total > 0 ? (covered / total) * 100 : 0;
}

// Calculate area of reservoir above minimum depth threshold (sampling)
function effectiveAreaM2(minDepthM: number): number {
  const STEP = 3;
  let count = 0;
  for (let y = 18; y < 440; y += STEP) {
    for (let x = 34; x < 175; x += STEP) {
      if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
      if (getDepthAt(x, y) >= minDepthM) count++;
    }
  }
  return count * STEP * STEP;
}

// Calculate average depth across the reservoir (volume-weighted approximation)
function meanReservoirDepth(): number {
  const STEP = 4;
  let sumDepth = 0, count = 0;
  for (let y = 18; y < 440; y += STEP) {
    for (let x = 34; x < 175; x += STEP) {
      if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
      sumDepth += getDepthAt(x, y);
      count++;
    }
  }
  return count > 0 ? sumDepth / count : 0;
}

// Reservoir volume estimate (m³)
function reservoirVolumeM3(): number {
  const STEP = 3;
  let sumDepth = 0;
  for (let y = 18; y < 440; y += STEP) {
    for (let x = 34; x < 175; x += STEP) {
      if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
      sumDepth += getDepthAt(x, y);
    }
  }
  return sumDepth * STEP * STEP;
}

// ─── Optimization engine ──────────────────────────────────────────────────────
// Combines SOD demand satisfaction + destratification coverage to recommend
// optimal module count and spacing.
//
// References:
//   - Wüest et al. (1992)  — induced mixing radius ≈ 4–6 × H_eff
//   - Schladow (1993)      — destratification design methodology
//   - Lemckert & Imberger (1993) — plume horizontal extent in stratified water
//   - AWWA Manual M37 (2011) — 1–4 aerator units/ha for shallow reservoirs
//   - Hickey & Gibbs (2009) — SOD typical range 0.5–3.0 g O₂/m²/d
//
// SAFETY: applies 1.3× margin on AOTR-required count for diurnal/seasonal swings.

export interface Optimization {
  // Inputs
  reservoirAreaHa: number;
  effectiveAreaHa: number;
  meanDepthM: number;
  perModuleAOTR: number;     // kg O₂/h per module
  sodGOM2D: number;          // assumed SOD rate
  targetCoveragePct: number;
  // Outputs
  totalSODkgh: number;       // total SOD demand for entire reservoir
  totalSODwithSafetyKgh: number;
  modulesBySOD: number;
  rMixM: number;             // destratification mixing radius
  areaPerModuleM2: number;   // effective destratification area per module
  modulesByCoverage: number;
  stricterCount: number;     // max(modsBySOD, modsByCov) — un-capped
  awwaMaxCount: number;      // AWWA upper cap (6/ha)
  awwaMinCount: number;      // AWWA floor (1/ha)
  capBinding: boolean;       // true if AWWA cap is what's limiting the count
  recommendedCount: number;
  recommendedSpacingM: number;
  recommendedHexSpacingM: number;
  minSpacingM: number;
  maxSpacingM: number;
  modulesPerHa: number;
  totalAirflowNm3h: number;
  totalAOTRkgh: number;
  o2Surplus: number;
  expectedCoveragePct: number;
}

function optimizeLayout(
  reservoirAreaM2: number,
  effectiveAreaM2_: number,
  meanDepthM: number,
  perModuleAOTR: number,
  perModuleAirflow: number,
  sodGOM2D: number,
  targetCoveragePct: number,
  safetyFactor: number = 1.3,
): Optimization {
  const reservoirAreaHa = reservoirAreaM2 / 10_000;
  const effectiveAreaHa = effectiveAreaM2_ / 10_000;

  // 1. SOD demand (entire reservoir)
  const totalSODkgh           = (reservoirAreaM2 * sodGOM2D) / (1000 * 24);
  const totalSODwithSafetyKgh = totalSODkgh * safetyFactor;
  const modulesBySOD          = Math.max(1, Math.ceil(totalSODwithSafetyKgh / Math.max(0.001, perModuleAOTR)));

  // 2. Destratification coverage (Wüest 1992 / Lemckert & Imberger 1993)
  // For SHALLOW reservoirs (H_mean < 5 m), one bubble plume induces whole-water
  // circulation cell ~8 x depth radius (more aggressive than 5 x H point-source).
  // Hex-packing efficiency 0.85 — modules can be sparser as plumes interact.
  const rMixM            = 8 * meanDepthM;
  const areaPerModuleM2  = Math.PI * rMixM * rMixM * 0.85;
  const targetCoverArea  = effectiveAreaM2_ * targetCoveragePct / 100;
  const modulesByCoverage = Math.max(1, Math.ceil(targetCoverArea / areaPerModuleM2));

  // 3. Recommended = stricter of constraints, BUT capped at AWWA M37 (2011)
  const reservoirAreaHa_calc = reservoirAreaM2 / 10_000;
  const awwaMaxCount = Math.ceil(reservoirAreaHa_calc * 6);
  const awwaMinCount = Math.max(2, Math.ceil(reservoirAreaHa_calc * 1));
  const stricterCount = Math.max(modulesBySOD, modulesByCoverage);
  const recommendedCount = Math.min(awwaMaxCount, Math.max(awwaMinCount, stricterCount));
  // True if the AWWA cap is the binding constraint (count = awwaMax and stricter would have been higher)
  const capBinding = stricterCount > awwaMaxCount;

  // 4. Spacing for that count over the EFFECTIVE area (≥ minDepth)
  const recommendedSpacingM    = Math.sqrt(effectiveAreaM2_ / recommendedCount);
  // Triangular/hex grid is √(2/√3) ≈ 1.0746× more efficient than square
  const recommendedHexSpacingM = recommendedSpacingM * 1.0746;

  // 5. Spacing bounds
  const r_plume_surface = 0.15 + 0.1 * meanDepthM;          // Seol 2007
  const minSpacingM     = Math.max(5, 2 * r_plume_surface);  // avoid plume merge
  const maxSpacingM     = 2 * rMixM;                         // maintain continuous coverage

  const modulesPerHa     = recommendedCount / reservoirAreaHa;
  const totalAirflowNm3h = recommendedCount * perModuleAirflow;
  const totalAOTRkgh     = recommendedCount * perModuleAOTR;
  const o2Surplus        = totalAOTRkgh - totalSODkgh;
  const expectedCoveragePct = Math.min(100, recommendedCount * areaPerModuleM2 / effectiveAreaM2_ * 100);

  return {
    reservoirAreaHa, effectiveAreaHa, meanDepthM, perModuleAOTR,
    sodGOM2D, targetCoveragePct,
    totalSODkgh, totalSODwithSafetyKgh, modulesBySOD,
    rMixM, areaPerModuleM2, modulesByCoverage,
    stricterCount, awwaMaxCount, awwaMinCount, capBinding,
    recommendedCount, recommendedSpacingM, recommendedHexSpacingM,
    minSpacingM, maxSpacingM, modulesPerHa,
    totalAirflowNm3h, totalAOTRkgh, o2Surplus, expectedCoveragePct,
  };
}

// ─── Module spec defaults ─────────────────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};

function loadSpec(): ModSpec {
  return loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS);
}

function fmt(v: number, dp = 2) { return isNaN(v) ? '—' : v.toFixed(dp); }
function fmtK(v: number) { return v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0); }

// ─── Zone depth legend (survey-matched colors) ────────────────────────────────
const LEGEND_BANDS = [
  { odnMin: 8.00, label: '≥ 8.00', depthLabel: '< 0.8 m', hex: '#e8e4b0' },
  { odnMin: 7.75, label: '7.75–7.99', depthLabel: '0.8–1.0 m', hex: '#d8d880' },
  { odnMin: 7.50, label: '7.50–7.74', depthLabel: '1.0–1.3 m', hex: '#c8d060' },
  { odnMin: 7.25, label: '7.25–7.49', depthLabel: '1.3–1.5 m', hex: '#b0c840' },
  { odnMin: 7.00, label: '7.00–7.24', depthLabel: '1.6–1.8 m', hex: '#88a830' },
  { odnMin: 6.75, label: '6.75–6.99', depthLabel: '1.8–2.0 m', hex: '#e8a8a0' },
  { odnMin: 6.50, label: '6.50–6.74', depthLabel: '2.1–2.3 m', hex: '#d88880' },
  { odnMin: 6.25, label: '6.25–6.49', depthLabel: '2.3–2.5 m', hex: '#c87060' },
  { odnMin: 6.00, label: '6.00–6.24', depthLabel: '2.6–2.8 m', hex: '#a05840' },
  { odnMin: 5.75, label: '5.75–5.99', depthLabel: '2.8–3.0 m', hex: '#60c890' },
  { odnMin: 5.50, label: '5.50–5.74', depthLabel: '3.1–3.3 m', hex: '#40a870' },
  { odnMin: 5.25, label: '5.25–5.49', depthLabel: '3.3–3.5 m', hex: '#309860' },
  { odnMin: 5.00, label: '5.00–5.24', depthLabel: '3.6–3.8 m', hex: '#208850' },
  { odnMin: 4.75, label: '4.75–4.99', depthLabel: '3.8–4.0 m', hex: '#80a8e0' },
  { odnMin: 4.50, label: '4.50–4.74', depthLabel: '4.1–4.3 m', hex: '#6088c8' },
  { odnMin: 4.25, label: '4.25–4.49', depthLabel: '4.3–4.5 m', hex: '#4068b0' },
  { odnMin: 4.00, label: '4.00–4.24', depthLabel: '4.6–4.8 m', hex: '#204898' },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function ReservoirLayoutPage() {
  const navigate = useNavigate();
  const baseSpec = useMemo(loadSpec, []);
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);
  const spec = useMemo(() => ({
    ...baseSpec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  }), [baseSpec, profile]);

  const [spacingM,     setSpacingM]     = useState(30);
  const [minDepthM,    setMinDepthM]    = useState(2.0);
  const [circRadM,     setCircRadM]     = useState(15);
  const [showCoverage, setShowCoverage] = useState(true);
  const [showZones,    setShowZones]    = useState(true);
  const [showGrid,     setShowGrid]     = useState(false);
  const [activeTab,    setActiveTab]    = useState<'map' | 'analysis' | 'recommend' | 'refs'>('map');

  // Optimization inputs (user-tunable)
  const [sodRate,         setSodRate]         = useState(2.0);   // g O₂/m²/d
  const [targetCoverage,  setTargetCoverage]  = useState(70);    // %
  const [safetyFactor,    setSafetyFactor]    = useState(1.3);   // SOD safety margin

  // Physics for the chosen module design (type-aware)
  const physics = useMemo(() => {
    const T         = spec.waterTempC;
    const nUnits    = unitsPerModule(spec.diffuserType, spec);
    const pores     = diffuserPores(spec.diffuserType, spec.diffuserDiamMm, spec) * nUnits;
    const qTotal    = spec.airflowPerDiffNm3h * nUnits;
    const d_b       = bubbleDiamMm(spec.poreSizeUm, qTotal, pores, T);
    const soteM     = sotePerM(d_b);
    const riseV     = riseVel(d_b, T);
    // Use depth from module spec for per-module physics; placement at actual site depth
    const effSubm   = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);
    const sotr      = qTotal * RHO_AIR_STD * O2_MASS_FRAC * (soteM * effSubm / 100);
    // AOTR at the CURRENT pre-aeration DO (displayed in tables — operational reality)
    const aotr      = sotr * spec.alphaFactor * spec.betaFactor
                      * Math.pow(1.024, T - 20)
                      * Math.max(0, doSat(T) - spec.currentDoMgL) / DO_SAT_20;
    // AOTR at a TARGET WORKING DO of 4 mg/L (the level the system maintains under
    // sustained aeration). This is the correct basis for SOD-supply sizing because
    // it represents long-run steady-state oxygen delivery, not the tiny transient
    // delivery available when water is already near saturation pre-aeration.
    // Reference: Metcalf & Eddy (2014) §5.6 — "design AOTR for sizing must use
    // the maintained operating DO, not the deficit at start-up".
    const TARGET_WORKING_DO_MGL = 4.0;
    const aotrDesign = sotr * spec.alphaFactor * spec.betaFactor
                       * Math.pow(1.024, T - 20)
                       * Math.max(0.5, doSat(T) - TARGET_WORKING_DO_MGL) / DO_SAT_20;
    const kw        = blowerKw(qTotal, effSubm, spec.diffuserDropKpa, spec.safetyPct, spec.blowerEff);
    const r_diff    = spec.diffuserDiamMm / 2000;
    const r_inf_bed = Math.max(r_diff, 0.35 * effSubm);
    const r_surface = r_diff + 0.1 * effSubm;
    const r_module  = (FRAME_SIZE / Math.SQRT2) + r_inf_bed;
    return { T, qTotal, d_b, soteM, riseV, effSubm, sotr, aotr, aotrDesign, kw, r_inf_bed, r_surface, r_module };
  }, [spec]);

  const reservoirAreaM2 = useMemo(() => polygonAreaM2(RESERVOIR_POLY), []);
  const reservoirAreaHa = reservoirAreaM2 / 10_000;
  const reservoirVolM3  = useMemo(reservoirVolumeM3, []);
  const meanDepth       = useMemo(meanReservoirDepth, []);
  const effAreaM2       = useMemo(() => effectiveAreaM2(minDepthM), [minDepthM]);

  // Optimization recommendation
  // Use DESIGN AOTR (at 4 mg/L target operating DO) for SOD sizing — NOT the
  // pre-aeration AOTR which is microscopic when baseline DO is already high.
  const opt = useMemo(() => optimizeLayout(
    reservoirAreaM2, effAreaM2, meanDepth,
    physics.aotrDesign, physics.qTotal,
    sodRate, targetCoverage, safetyFactor,
  ), [reservoirAreaM2, effAreaM2, meanDepth, physics.aotrDesign, physics.qTotal, sodRate, targetCoverage, safetyFactor]);

  // Use the OPTIMIZED count (capped AWWA) and distribute across the 8 named
  // array zones - NOT a grid. Matches PDF reports exactly so the layout
  // shown on screen is identical to what the customer receives.
  const modules = useMemo(
    () => placeModulesInZones(opt.recommendedCount, minDepthM),
    [opt.recommendedCount, minDepthM],
  );

  const coverage = useMemo(
    () => calcCoverage(modules, circRadM),
    [modules, circRadM]
  );

  const totals = useMemo(() => {
    const n = modules.length;
    if (n === 0) return { n: 0, kw: 0, sotr: 0, aotr: 0, qTotal: 0, avgDepth: 0, modulesPerHa: 0 };
    const avgDepth = modules.reduce((s, m) => s + m.depth, 0) / n;
    const kw   = n * physics.kw;
    const sotr = n * physics.sotr;
    const aotr = n * physics.aotr;
    const qT   = n * physics.qTotal;
    return { n, kw, sotr, aotr, qTotal: qT, avgDepth, modulesPerHa: n / reservoirAreaHa };
  }, [modules, physics, reservoirAreaHa]);

  // ─── PDF download (with recommendations rationale) ────────────────────────
  const downloadPDF = () => {
    const reasons = [
      `SOD demand: ${reservoirAreaHa.toFixed(2)} ha x ${sodRate} g/m2/d / 24 = ${opt.totalSODkgh.toFixed(2)} kg O2/h. With ${safetyFactor}x safety factor = ${opt.totalSODwithSafetyKgh.toFixed(2)} kg O2/h. Sizing uses DESIGN AOTR at target working DO 4 mg/L of ${fmt(physics.aotrDesign, 3)} kg O2/h per module (Metcalf & Eddy 2014 sizing convention). This requires ${opt.modulesBySOD} modules. Note: pre-aeration AOTR at the current DO of ${fmt(spec.currentDoMgL,1)} mg/L is only ${fmt(physics.aotr, 3)} kg O2/h because the deficit is small.`,
      `Destratification coverage: Mean depth ${meanDepth.toFixed(2)} m gives mixing radius r_mix = 8 x H = ${opt.rMixM.toFixed(1)} m (Lemckert & Imberger 1993, shallow reservoir adjustment to Wuest 1992). Each module covers pi x r^2 x 0.85 = ${opt.areaPerModuleM2.toFixed(0)} m^2 effective. To achieve ${targetCoverage}% coverage of ${(effAreaM2 / 10000).toFixed(2)} ha effective aeration area requires ${opt.modulesByCoverage} modules.`,
      `Stricter constraint: max(${opt.modulesBySOD}, ${opt.modulesByCoverage}) = ${Math.max(opt.modulesBySOD, opt.modulesByCoverage)} modules (${opt.modulesBySOD >= opt.modulesByCoverage ? 'oxygen-supply' : 'destratification-coverage'} constraint dominates).`,
      `AWWA M37 (2011) cap: For shallow reservoir destratification, AWWA upper guidance is 6 modules/ha = ${Math.ceil(reservoirAreaHa * 6)} modules for ${reservoirAreaHa.toFixed(2)} ha. Lower bound 1/ha = ${Math.max(2, Math.ceil(reservoirAreaHa * 1))} modules. Final recommended = min(cap, max(min, stricter)) = ${opt.recommendedCount} modules.`,
      `Spacing: with ${opt.recommendedCount} modules across the ${(effAreaM2 / 10000).toFixed(2)} ha aeration zone, square-grid spacing is ${opt.recommendedSpacingM.toFixed(1)} m. Hex-grid alternative is ${opt.recommendedHexSpacingM.toFixed(1)} m (7.5% more efficient packing).`,
      `Bounds check: spacing must stay between ${opt.minSpacingM.toFixed(1)} m (avoid plume merge) and ${opt.maxSpacingM.toFixed(1)} m (continuous coverage). Recommended spacing is ${opt.recommendedSpacingM >= opt.minSpacingM && opt.recommendedSpacingM <= opt.maxSpacingM ? 'within bounds.' : 'outside bounds - review.'}`,
      `Density: ${(opt.recommendedCount / reservoirAreaHa).toFixed(1)} modules/ha. ${(opt.recommendedCount / reservoirAreaHa) >= 1 && (opt.recommendedCount / reservoirAreaHa) <= 4 ? 'Within the typical AWWA (2011) range of 1-4 modules/ha for shallow reservoir destratification.' : 'Outside the typical AWWA range - intensive or sparse aeration.'}`,
    ];

    // Module positions for plan view rendering — distribute modules over zones
    const ZONE_CENTRES = [
      { id: 'A', cx: 150, cy: 90,  depth: 4.4 },
      { id: 'B', cx: 92,  cy: 88,  depth: 3.5 },
      { id: 'C', cx: 62,  cy: 195, depth: 3.3 },
      { id: 'D', cx: 100, cy: 278, depth: 3.2 },
      { id: 'E', cx: 152, cy: 170, depth: 3.1 },
      { id: 'F', cx: 105, cy: 135, depth: 2.5 },
      { id: 'G', cx: 115, cy: 230, depth: 2.4 },
      { id: 'H', cx: 92,  cy: 340, depth: 2.3 },
    ];
    const perZone = Math.max(1, Math.ceil(opt.recommendedCount / ZONE_CENTRES.length));
    const modulePts: { x: number; y: number }[] = [];
    for (const z of ZONE_CENTRES) {
      const cols = Math.ceil(Math.sqrt(perZone));
      for (let i = 0; i < perZone && modulePts.length < opt.recommendedCount; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        modulePts.push({ x: z.cx + (col - cols / 2) * 6, y: z.cy + (row - cols / 2) * 5 });
      }
    }

    generateReservoirLayoutPDF({
      spec: { ...spec },
      reservoir: { areaHa: reservoirAreaHa, meanDepth, volumeM3: reservoirVolM3, effAreaHa: effAreaM2 / 10000 },
      layout: {
        currentSpacing: spacingM, currentMinDepth: minDepthM,
        currentModuleCount: modules.length, currentCoverage: coverage,
        sodRate, targetCoverage,
      },
      recommendations: {
        bestCount: opt.recommendedCount,
        bestSpacing: opt.recommendedSpacingM,
        bestHexSpacing: opt.recommendedHexSpacingM,
        modulesPerHa: opt.modulesPerHa,
        rMix: opt.rMixM,
        areaPerMod: opt.areaPerModuleM2,
        expectedCoverage: opt.expectedCoveragePct,
        totalAOTR: opt.totalAOTRkgh,
        totalAirflow: opt.totalAirflowNm3h,
        totalKw: opt.recommendedCount * physics.kw,
        o2Surplus: opt.o2Surplus,
        sodDemand: opt.totalSODkgh,
        sodWithSafety: opt.totalSODwithSafetyKgh,
        reasons,
      },
      arrayLabels: ZONE_CENTRES.map(z => ({
        id: z.id, x: z.cx, y: z.cy, depthM: z.depth, modulesAssigned: perZone,
      })),
      modules: modulePts,
    }, profile);
    toast.success(`Layout PDF downloaded (${profile.label})`);
  };

  const [showRecommendModal, setShowRecommendModal] = useState(false);

  // Zone breakdown
  const zoneStats = useMemo(() => {
    const zones = [
      { label: 'Very shallow < 1 m', color: '#c8d060', count: 0 },
      { label: 'Shallow 1–2 m',      color: '#c87060', count: 0 },
      { label: 'Medium 2–3 m',       color: '#a05840', count: 0 },
      { label: 'Deep 3–4 m',         color: '#309860', count: 0 },
      { label: 'Deepest > 4 m',      color: '#4068b0', count: 0 },
    ];
    for (const m of modules) {
      if      (m.depth < 1.0) zones[0].count++;
      else if (m.depth < 2.0) zones[1].count++;
      else if (m.depth < 3.0) zones[2].count++;
      else if (m.depth < 4.0) zones[3].count++;
      else                    zones[4].count++;
    }
    return zones.filter(z => z.count > 0);
  }, [modules]);

  // SVG helpers
  const VIEW_W = 220, VIEW_H = 480;
  const polyPts = RESERVOIR_POLY.map(p => p.join(',')).join(' ');

  const ZONE_COLORS: Record<string, string> = {
    main:   '#7a4a2a',  // 2-3m dominant (brownish, pre-dredge silt)
    deep:   '#2a6b4e',  // 3-4m (teal-green)
    deepst: '#2a4488',  // 4-4.8m (blue)
    edge:   '#6a6a28',  // <1.5m edge (olive)
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader
        title="Reservoir Layout"
        subtitle="Barcombe Reservoir · J2020 pre-dredge bathymetric survey · module placement"
        icon={MapPin}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={downloadPDF}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rb-brand text-slate-900 hover:bg-rb-brand/90 transition-all font-bold shadow"
            >
              <ClipboardCheck className="w-3.5 h-3.5" /> Download Layout PDF
            </button>
            <button
              onClick={() => setShowRecommendModal(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 hover:bg-emerald-500/90 transition-all font-bold shadow"
            >
              <Sparkles className="w-3.5 h-3.5" /> Recommended Options
            </button>
            <button
              onClick={() => navigate('/sediment-bed-physics')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 transition-all font-semibold"
            >
              <Layers className="w-3.5 h-3.5" /> Bed Physics
            </button>
            <button
              onClick={() => navigate('/reservoir-steps')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-all font-semibold"
            >
              <ClipboardCheck className="w-3.5 h-3.5" /> Calculation Steps &amp; PDF
            </button>
            <button
              onClick={() => navigate('/reservoir-validation')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rb-brand/15 text-rb-brand border border-rb-brand/30 hover:bg-rb-brand/25 transition-all font-semibold"
            >
              <ClipboardCheck className="w-3.5 h-3.5" /> Validation Report
            </button>
            <button
              onClick={() => navigate('/diffuser-module')}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Diffuser Module
            </button>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="flex gap-1 px-4 pt-3 pb-0 border-b border-slate-800/60">
        {(['map', 'recommend', 'analysis', 'refs'] as const).map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-3 py-1.5 text-xs rounded-t-lg transition-all capitalize ${
              activeTab === t
                ? 'bg-slate-800 text-slate-100 border-b-2 border-rb-brand'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {t === 'map' ? 'Plan View'
              : t === 'recommend' ? 'Recommendation Engine'
              : t === 'analysis' ? 'Zone Analysis' : 'References'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">

        {/* ── SURVEY BANNER ─────────────────────────────────────────────────── */}
        <div className="rb-card-sm p-3 flex flex-wrap gap-4 text-[10px] text-slate-400">
          <span><span className="text-slate-300 font-semibold">Survey</span> Shoreline Surveys Ltd · J2020 · 09 Jan 2023</span>
          <span><span className="text-slate-300 font-semibold">Client</span> Atkins Réalis</span>
          <span><span className="text-slate-300 font-semibold">Datum</span> ODN Newlyn · Water level {WATER_LEVEL_ODN} m ODN</span>
          <span><span className="text-slate-300 font-semibold">Depth range</span> 0.79 – 4.79 m · Scale 1:1,000 on A1</span>
          <span><span className="text-slate-300 font-semibold">Grid</span> OSGB36 OSTN15 · 543,750 E · 115,000 N</span>
        </div>

        {/* ══ MAP TAB ═══════════════════════════════════════════════════════ */}
        {activeTab === 'map' && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">

            {/* ── SVG Map ──────────────────────────────────────────────────── */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-rb-brand" /> Bathymetric Plan — Module Placement
              </h3>

              <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                className="w-full border border-slate-700/50 rounded-xl"
                style={{ background: '#0d1420', maxHeight: '76vh' }}
              >
                <defs>
                  <clipPath id="res-clip">
                    <polygon points={polyPts} />
                  </clipPath>
                </defs>

                {/* ── Grid lines (50 m spacing) ── */}
                {showGrid && Array.from({ length: 5 }, (_, i) => (i + 1) * 50).map(v => (
                  <g key={v} stroke="#1e2a3a" strokeWidth="0.4">
                    <line x1={v} y1={0} x2={v} y2={VIEW_H} />
                    <line x1={0} y1={v} x2={VIEW_W} y2={v} />
                  </g>
                ))}

                {/* ── Reservoir base fill (dominant 2-3m zone) ── */}
                <polygon points={polyPts} fill={ZONE_COLORS.main} opacity="0.9" />

                {/* ── Depth zone overlays (clipped) ── */}
                {showZones && (
                  <g clipPath="url(#res-clip)">
                    {/* Shallow edge band — approximate via stroke on outline */}
                    <polygon points={polyPts} fill="none" stroke="#8a8a28" strokeWidth="14" opacity="0.5" />
                    {/* Deep zone ellipses */}
                    {DEPTH_ZONE_ELLIPSES.map((z, i) => (
                      <ellipse key={i} cx={z.cx} cy={z.cy} rx={z.rx} ry={z.ry}
                        fill={z.svgColor} opacity="0.85" />
                    ))}
                  </g>
                )}

                {/* ── Reservoir outline ── */}
                <polygon points={polyPts} fill="none" stroke="#d2823a" strokeWidth="1.2" />

                {/* ── Coverage / circulation circles ── */}
                {showCoverage && modules.map((m, i) => (
                  <circle key={i} cx={m.x} cy={m.y} r={circRadM}
                    fill="#cda42e" fillOpacity="0.06" stroke="#cda42e" strokeOpacity="0.18" strokeWidth="0.5" />
                ))}

                {/* ── Module positions (placed at named array zones, not grid) ── */}
                {modules.map((m, i) => {
                  const col = m.depth >= 4.0 ? '#4488ff'
                            : m.depth >= 3.0 ? '#38c878'
                            : '#cda42e';
                  return (
                    <g key={i}>
                      {/* 2×2m frame indicator */}
                      <rect x={m.x - 1} y={m.y - 1} width={2} height={2}
                        fill={col} fillOpacity="0.45" stroke={col} strokeWidth="0.3" />
                      <circle cx={m.x} cy={m.y} r={1.2} fill={col} />
                    </g>
                  );
                })}

                {/* ── Array zone labels (A–H) with centre badge ── */}
                {ARRAY_ZONE_CENTRES.filter(z => z.depth >= minDepthM).map(z => (
                  <g key={`lbl-${z.id}`}>
                    {/* Zone dashed outline (10m radius) */}
                    <circle cx={z.cx} cy={z.cy} r={10}
                      fill="none" stroke="#cda42e" strokeWidth="0.4"
                      strokeDasharray="2,2" opacity="0.55" />
                    {/* Centre badge */}
                    <circle cx={z.cx} cy={z.cy} r={5}
                      fill="#0d1420" fillOpacity="0.92" stroke="#cda42e" strokeWidth="0.7" />
                    <text x={z.cx} y={z.cy + 2} textAnchor="middle"
                      fontSize="6" fontWeight="bold" fill="#cda42e">{z.id}</text>
                  </g>
                ))}

                {/* ── Scale bar (50 m) ── */}
                <g transform="translate(10,460)">
                  <line x1={0} y1={0} x2={50} y2={0} stroke="#94a3b8" strokeWidth="1.5" />
                  <line x1={0} y1={-3} x2={0} y2={3} stroke="#94a3b8" strokeWidth="1.5" />
                  <line x1={50} y1={-3} x2={50} y2={3} stroke="#94a3b8" strokeWidth="1.5" />
                  <text x={25} y={-5} textAnchor="middle" fontSize="5" fill="#94a3b8">50 m</text>
                </g>

                {/* ── North arrow ── */}
                <g transform="translate(197,20)">
                  <polygon points="0,-8 -3,4 0,2 3,4" fill="#94a3b8" />
                  <text x={0} y={-11} textAnchor="middle" fontSize="4.5" fill="#94a3b8" fontWeight="bold">N</text>
                </g>

                {/* ── Depth zone legend (in-map) ── */}
                {showZones && (
                  <g transform="translate(8,20)">
                    <rect x={-2} y={-2} width={52} height={46} rx={2}
                      fill="#0d1420" fillOpacity="0.75" />
                    {[
                      { col: '#8a8a28', lbl: '< 1.5 m' },
                      { col: ZONE_COLORS.main, lbl: '2–3 m' },
                      { col: DEPTH_ZONE_ELLIPSES[0].svgColor, lbl: '3–4 m' },
                      { col: DEPTH_ZONE_ELLIPSES[4].svgColor, lbl: '4–4.8 m' },
                    ].map((item, idx) => (
                      <g key={idx} transform={`translate(0,${idx * 10})`}>
                        <rect width={7} height={7} fill={item.col} rx={1} />
                        <text x={9} y={6} fontSize="5" fill="#94a3b8">{item.lbl}</text>
                      </g>
                    ))}
                  </g>
                )}

                {/* ── Module count label ── */}
                <text x={VIEW_W / 2} y={VIEW_H - 4} textAnchor="middle" fontSize="5" fill="#64748b">
                  {modules.length} modules · {spacingM} m spacing · min {minDepthM} m depth
                </text>
              </svg>
            </div>

            {/* ── Right panel ──────────────────────────────────────────────── */}
            <div className="space-y-4">

              {/* Controls */}
              <div className="rb-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-rb-brand" /> Placement Controls
                </h3>

                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Module spacing</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[15, 20, 25, 30, 40, 50].map(v => (
                      <button key={v} onClick={() => setSpacingM(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          spacingM === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                        : 'text-slate-400 hover:text-slate-200 border border-slate-700/50 hover:border-slate-600'
                        }`}>{v} m</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Minimum depth</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[1.5, 2.0, 2.5, 3.0].map(v => (
                      <button key={v} onClick={() => setMinDepthM(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          minDepthM === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                         : 'text-slate-400 hover:text-slate-200 border border-slate-700/50 hover:border-slate-600'
                        }`}>{v} m</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Circulation radius</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[5, 10, 15, 20, 30].map(v => (
                      <button key={v} onClick={() => setCircRadM(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          circRadM === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                        : 'text-slate-400 hover:text-slate-200 border border-slate-700/50 hover:border-slate-600'
                        }`}>{v} m</button>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-600 mt-1">
                    Lemckert &amp; Imberger (1993): destratification radius ≈ 5×depth = {(5 * totals.avgDepth).toFixed(0)} m
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 pt-1">
                  {[
                    { label: 'Coverage circles', val: showCoverage, set: setShowCoverage },
                    { label: 'Depth zones',      val: showZones,    set: setShowZones },
                    { label: '50 m grid',        val: showGrid,     set: setShowGrid },
                  ].map(({ label, val, set }) => (
                    <label key={label} className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
                      <input type="checkbox" checked={val} onChange={e => set(e.target.checked)}
                        className="accent-rb-brand" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Key stats */}
              <div className="rb-card p-4">
                <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-rb-brand" /> Summary
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { lbl: 'Reservoir area',  val: `${reservoirAreaHa.toFixed(1)} ha` },
                    { lbl: 'Modules placed',  val: `${totals.n}` },
                    { lbl: 'Modules/ha',      val: fmt(totals.modulesPerHa, 1) },
                    { lbl: 'Avg depth',       val: `${fmt(totals.avgDepth, 1)} m` },
                    { lbl: 'Coverage',        val: `${coverage.toFixed(0)} %` },
                    { lbl: 'Total airflow',   val: `${fmt(totals.qTotal, 0)} Nm³/h` },
                    { lbl: 'Total SOTR',      val: `${fmt(totals.sotr, 1)} kg O₂/h` },
                    { lbl: 'Total AOTR',      val: `${fmt(totals.aotr, 1)} kg O₂/h` },
                    { lbl: 'Total power',     val: `${fmt(totals.kw, 1)} kW` },
                    { lbl: 'Bubble diam',     val: `${fmt(physics.d_b, 2)} mm` },
                    { lbl: 'SOTE/m',          val: `${fmt(physics.soteM, 2)} %/m` },
                    { lbl: 'Rise velocity',   val: `${fmt(physics.riseV, 3)} m/s` },
                  ].map(({ lbl, val }) => (
                    <div key={lbl} className="bg-slate-800/40 rounded-lg p-2">
                      <div className="text-[10px] text-slate-500">{lbl}</div>
                      <div className="text-sm font-semibold text-slate-100">{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Zone breakdown */}
              {zoneStats.length > 0 && (
                <div className="rb-card p-4">
                  <h3 className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Modules by depth zone</h3>
                  <div className="space-y-1.5">
                    {zoneStats.map(z => (
                      <div key={z.label} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm shrink-0" style={{ background: z.color }} />
                        <span className="text-[11px] text-slate-400 flex-1">{z.label}</span>
                        <span className="text-[11px] font-semibold text-slate-200">{z.count}</span>
                        <span className="text-[10px] text-slate-600">
                          ({totals.n > 0 ? ((z.count / totals.n) * 100).toFixed(0) : 0}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Depth colour key (survey-matched) */}
              <div className="rb-card p-4">
                <h3 className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Survey depth colour key</h3>
                <div className="space-y-0.5">
                  {LEGEND_BANDS.map(b => (
                    <div key={b.label} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm shrink-0" style={{ background: b.hex }} />
                      <span className="text-[9px] text-slate-500 w-20">{b.label} m ODN</span>
                      <span className="text-[9px] text-slate-400">{b.depthLabel}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] text-slate-600 mt-2">
                  Water level {WATER_LEVEL_ODN} m ODN · depth = {WATER_LEVEL_ODN} − ODN elevation
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ══ RECOMMENDATION ENGINE TAB ══════════════════════════════════════ */}
        {activeTab === 'recommend' && (
          <div className="space-y-4 max-w-5xl">

            {/* Hero recommendation box */}
            <div className="rb-card p-5 border-2 border-rb-brand/30 bg-gradient-to-br from-rb-brand/5 to-transparent">
              <div className="flex items-start gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-rb-brand shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-base font-bold text-slate-100">Optimal Layout Recommendation</h2>
                  <p className="text-[11px] text-slate-400">
                    Combined SOD-demand &amp; destratification-coverage optimisation for Barcombe Reservoir
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                <div className="bg-slate-900/60 border border-rb-brand/20 rounded-xl p-3 text-center">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider">Modules</div>
                  <div className="text-2xl font-black text-rb-brand">{opt.recommendedCount}</div>
                  <div className="text-[9px] text-slate-600">{fmt(opt.modulesPerHa, 1)}/ha</div>
                </div>
                <div className="bg-slate-900/60 border border-slate-700/40 rounded-xl p-3 text-center">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider">Spacing</div>
                  <div className="text-2xl font-black text-slate-100">{fmt(opt.recommendedSpacingM, 0)}</div>
                  <div className="text-[9px] text-slate-600">m (square grid)</div>
                </div>
                <div className="bg-slate-900/60 border border-slate-700/40 rounded-xl p-3 text-center">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider" title="Mixing coverage — % of bed within plume mixing radius">
                    Mixing coverage
                  </div>
                  <div className="text-2xl font-black text-emerald-400">{fmt(opt.expectedCoveragePct, 0)}%</div>
                  <div className="text-[9px] text-slate-600">vs {targetCoverage}% target</div>
                </div>
                <div className={`rounded-xl p-3 text-center border ${
                  opt.o2Surplus >= 0
                    ? 'bg-slate-900/60 border-emerald-500/30'
                    : 'bg-red-500/10 border-red-500/40'
                }`}>
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider"
                       title="O2 balance — total system AOTR minus SOD demand (kg O2/h)">
                    O₂ balance
                  </div>
                  <div className={`text-2xl font-black ${opt.o2Surplus >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {opt.o2Surplus >= 0 ? '+' : ''}{fmt(opt.o2Surplus, 1)}
                  </div>
                  <div className="text-[9px] text-slate-600">kg O₂/h</div>
                </div>
              </div>

              {/* Deficit warning — different metric from coverage */}
              {opt.o2Surplus < 0 && (
                <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-[11px] text-slate-300 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-red-300">O₂ supply DEFICIT — coverage and O₂ balance are different metrics:</strong>
                      <div className="mt-1.5 text-slate-400 text-[10px]">
                        <strong>Mixing coverage ({fmt(opt.expectedCoveragePct, 0)}%)</strong> measures the bed area within a module's plume mixing radius — physical destratification.<br />
                        <strong>O₂ balance ({fmt(opt.o2Surplus, 2)} kg O₂/h)</strong> measures total system AOTR minus SOD demand — oxygen mass balance.
                      </div>
                      <div className="mt-2 text-amber-200">
                        The {opt.recommendedCount} recommended modules mix the water column adequately, but their combined design AOTR ({fmt(opt.totalAOTRkgh, 2)} kg O₂/h) cannot meet the SOD demand ({fmt(opt.totalSODkgh, 2)} kg O₂/h). Deficit: <strong>{fmt(Math.abs(opt.o2Surplus), 2)} kg O₂/h ({fmt(-opt.o2Surplus / opt.totalSODkgh * 100, 0)}% short)</strong>.
                      </div>
                      <div className="mt-2 text-slate-300">
                        To close the gap, choose one of the following:
                      </div>
                      <ul className="list-disc list-inside ml-2 mt-1 space-y-0.5 text-slate-400">
                        <li><strong className="text-slate-200">Increase airflow per diffuser</strong> on Diffuser Module page (currently {fmt(spec.airflowPerDiffNm3h, 2)} Nm³/h — try {fmt(spec.airflowPerDiffNm3h * Math.abs(opt.totalSODkgh / Math.max(0.01, opt.totalAOTRkgh)), 2)} Nm³/h)</li>
                        <li><strong className="text-slate-200">Increase diffusers per module</strong> on Diffuser Module page (more diffusers → higher per-module AOTR)</li>
                        <li><strong className="text-slate-200">Lower the SOD rate assumption</strong> ({sodRate} g O₂/m²/d) if site data supports a less conservative value</li>
                        <li><strong className="text-slate-200">Accept the deficit</strong> — operational DO will stabilise below the 4 mg/L target. The reservoir will still benefit from {fmt(opt.expectedCoveragePct, 0)}% mixing coverage.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  setSpacingM(Math.round(opt.recommendedSpacingM / 5) * 5);
                  setActiveTab('map');
                }}
                className="w-full px-4 py-2.5 rounded-xl bg-rb-brand text-slate-900 font-bold text-xs hover:bg-rb-brand/90 transition-all shadow-lg shadow-amber-900/20 flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" /> Apply Optimal Layout to Plan View
              </button>
              <p className="text-[10px] text-slate-500 mt-2 text-center">
                Will set spacing to {Math.round(opt.recommendedSpacingM / 5) * 5} m (snapped to 5 m grid).
                Adjust minimum depth manually as needed.
              </p>
            </div>

            {/* Optimization inputs */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4 text-rb-brand" /> Optimisation Inputs
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">SOD rate (g O₂/m²/d)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[0.5, 1.0, 1.5, 2.0, 2.5, 3.0].map(v => (
                      <button key={v} onClick={() => setSodRate(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          sodRate === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                       : 'text-slate-400 hover:text-slate-200 border border-slate-700/50'
                        }`}>{v}</button>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-600 mt-1">
                    Hickey &amp; Gibbs (2009): 0.5–3.0; Barcombe pre-dredge: 1.5–2.5
                  </p>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Target coverage (%)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[50, 60, 70, 80, 90].map(v => (
                      <button key={v} onClick={() => setTargetCoverage(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          targetCoverage === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                              : 'text-slate-400 hover:text-slate-200 border border-slate-700/50'
                        }`}>{v}%</button>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-600 mt-1">
                    AWWA (2011): 60–80% for destratification of shallow reservoirs
                  </p>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Safety factor</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[1.0, 1.2, 1.3, 1.5, 2.0].map(v => (
                      <button key={v} onClick={() => setSafetyFactor(v)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all ${
                          safetyFactor === v ? 'bg-rb-brand/20 text-rb-brand border border-rb-brand/30'
                                            : 'text-slate-400 hover:text-slate-200 border border-slate-700/50'
                        }`}>×{v}</button>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-600 mt-1">
                    Margin for diurnal/seasonal swings (default 1.3×)
                  </p>
                </div>
              </div>
            </div>

            {/* Derivation breakdown */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-rb-brand" /> Module Count Derivation
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* SOD-based */}
                <div className="rounded-xl border border-slate-700/40 bg-slate-800/30 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Wind className="w-4 h-4 text-cyan-400" />
                    <h4 className="text-xs font-bold text-slate-200">By SOD Demand</h4>
                  </div>
                  <div className="space-y-1.5 text-[10px] font-mono text-slate-300">
                    <div className="flex justify-between"><span>Reservoir area</span><span>{fmt(opt.reservoirAreaHa, 2)} ha</span></div>
                    <div className="flex justify-between"><span>SOD rate</span><span>{fmt(sodRate, 1)} g O₂/m²/d</span></div>
                    <div className="flex justify-between"><span>Total SOD demand</span><span>{fmt(opt.totalSODkgh, 2)} kg O₂/h</span></div>
                    <div className="flex justify-between"><span>× Safety factor</span><span>×{fmt(safetyFactor, 1)}</span></div>
                    <div className="flex justify-between text-amber-300"><span>SOD with safety</span><span>{fmt(opt.totalSODwithSafetyKgh, 2)} kg O₂/h</span></div>
                    <div className="flex justify-between"><span>÷ Design AOTR (at 4 mg/L)</span><span>÷{fmt(physics.aotrDesign, 3)} kg O₂/h</span></div>
                    <div className="flex justify-between border-t border-slate-700/40 pt-1.5 text-emerald-300 font-bold">
                      <span>= Modules required</span><span>{opt.modulesBySOD}</span>
                    </div>
                  </div>
                </div>

                {/* Coverage-based */}
                <div className="rounded-xl border border-slate-700/40 bg-slate-800/30 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-emerald-400" />
                    <h4 className="text-xs font-bold text-slate-200">By Destratification Coverage</h4>
                  </div>
                  <div className="space-y-1.5 text-[10px] font-mono text-slate-300">
                    <div className="flex justify-between"><span>Mean depth (sampled)</span><span>{fmt(opt.meanDepthM, 2)} m</span></div>
                    <div className="flex justify-between"><span>r_mix = 5 × H_mean</span><span>{fmt(opt.rMixM, 1)} m</span></div>
                    <div className="flex justify-between"><span>Area/module = π·r²·0.65</span><span>{fmt(opt.areaPerModuleM2, 0)} m²</span></div>
                    <div className="flex justify-between"><span>Effective area (≥{minDepthM}m)</span><span>{fmt(opt.effectiveAreaHa, 2)} ha</span></div>
                    <div className="flex justify-between"><span>× Target coverage</span><span>×{targetCoverage}%</span></div>
                    <div className="flex justify-between text-amber-300"><span>Target cover area</span><span>{fmt(opt.effectiveAreaHa * 10000 * targetCoverage / 100, 0)} m²</span></div>
                    <div className="flex justify-between border-t border-slate-700/40 pt-1.5 text-emerald-300 font-bold">
                      <span>= Modules required</span><span>{opt.modulesByCoverage}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendation reasoning */}
              <div className={`p-3 rounded-xl border ${
                opt.capBinding ? 'bg-amber-500/5 border-amber-500/30' : 'bg-emerald-500/5 border-emerald-500/20'
              }`}>
                <div className="flex items-start gap-2">
                  {opt.capBinding
                    ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    : <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />}
                  <div className="text-[11px] text-slate-300 leading-relaxed">
                    <div>
                      <strong className={opt.capBinding ? 'text-amber-300' : 'text-emerald-300'}>
                        Recommended: {opt.recommendedCount} modules
                      </strong>{' '}
                      ({fmt(opt.modulesPerHa, 1)} modules/ha)
                    </div>
                    <div className="mt-1.5 text-[10px] text-slate-400">
                      Stricter of constraints: max({opt.modulesBySOD}, {opt.modulesByCoverage}) = <strong className="text-slate-200">{opt.stricterCount}</strong>.
                      {' '}{opt.modulesBySOD >= opt.modulesByCoverage
                        ? `SOD-supply constraint dominates (oxygen demand).`
                        : `Coverage constraint dominates (destratification).`}
                    </div>
                    {opt.capBinding ? (
                      <div className="mt-2 text-[10px] text-amber-300">
                        <strong>⚠ AWWA cap binding:</strong> the un-capped requirement was <strong>{opt.stricterCount} modules</strong>{' '}
                        but AWWA M37 (2011) upper guidance limits shallow-reservoir aeration to 6 modules/ha = <strong>{opt.awwaMaxCount} modules</strong> for {fmt(opt.reservoirAreaHa, 2)} ha.{' '}
                        Changing SOD rate or target coverage won't reduce the count below this cap until those constraints fall below it.
                      </div>
                    ) : opt.recommendedCount === opt.awwaMinCount && opt.stricterCount < opt.awwaMinCount ? (
                      <div className="mt-2 text-[10px] text-slate-400">
                        <strong>Floor applied:</strong> raw count would be {opt.stricterCount} but AWWA minimum 1/ha = {opt.awwaMinCount} modules sets the floor.
                      </div>
                    ) : (
                      <div className="mt-2 text-[10px] text-emerald-400">
                        ✓ Constraints fit within AWWA range — recommended count is the actual stricter-constraint value.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Spacing analysis */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Ruler className="w-4 h-4 text-rb-brand" /> Spacing Optimisation
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="bg-slate-800/40 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Minimum</div>
                  <div className="text-xl font-bold text-amber-300">{fmt(opt.minSpacingM, 1)} m</div>
                  <div className="text-[9px] text-slate-600">2 × r_plume (no merge)</div>
                </div>
                <div className="bg-rb-brand/10 border border-rb-brand/30 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-rb-brand/80 uppercase tracking-wider">Recommended</div>
                  <div className="text-xl font-bold text-rb-brand">{fmt(opt.recommendedSpacingM, 1)} m</div>
                  <div className="text-[9px] text-slate-500">square grid</div>
                </div>
                <div className="bg-slate-800/40 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Maximum</div>
                  <div className="text-xl font-bold text-emerald-300">{fmt(opt.maxSpacingM, 1)} m</div>
                  <div className="text-[9px] text-slate-600">2 × r_mix (continuous cover)</div>
                </div>
              </div>

              <div className="text-[11px] text-slate-400 leading-relaxed">
                <p className="mb-2">
                  <strong className="text-slate-200">Square-grid spacing</strong> ({fmt(opt.recommendedSpacingM, 1)} m): simplest to deploy. Each module covers a square cell.
                </p>
                <p className="mb-2">
                  <strong className="text-slate-200">Hexagonal-grid spacing</strong> ({fmt(opt.recommendedHexSpacingM, 1)} m): 7.5% more efficient packing — same module count covers larger area, recommended for irregular reservoir shapes.
                </p>
                <p>
                  <strong className="text-slate-200">Sanity bounds:</strong> spacing must be ≥ {fmt(opt.minSpacingM, 1)} m to prevent adjacent plumes merging and creating recirculation dead zones (Schladow 1993), and ≤ {fmt(opt.maxSpacingM, 1)} m to maintain continuous destratification coverage (Wüest et al. 1992 mixing-radius criterion).
                </p>
              </div>
            </div>

            {/* Current vs recommended */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-rb-brand" /> Current Layout vs Recommendation
              </h3>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <th className="text-left py-1.5 text-slate-500 font-normal">Metric</th>
                    <th className="text-right py-1.5 text-slate-500 font-normal">Current</th>
                    <th className="text-right py-1.5 text-slate-500 font-normal">Recommended</th>
                    <th className="text-right py-1.5 text-slate-500 font-normal">Δ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {[
                    { lbl: 'Module count', cur: modules.length, rec: opt.recommendedCount, fmt: (v: number) => v.toFixed(0) },
                    { lbl: 'Modules/ha', cur: modules.length / opt.reservoirAreaHa, rec: opt.modulesPerHa, fmt: (v: number) => v.toFixed(1) },
                    { lbl: 'Spacing (m)', cur: spacingM, rec: opt.recommendedSpacingM, fmt: (v: number) => v.toFixed(0) },
                    { lbl: 'Total airflow (Nm³/h)', cur: modules.length * physics.qTotal, rec: opt.totalAirflowNm3h, fmt: (v: number) => v.toFixed(0) },
                    { lbl: 'Total AOTR (kg O₂/h)', cur: modules.length * physics.aotr, rec: opt.totalAOTRkgh, fmt: (v: number) => v.toFixed(2) },
                    { lbl: 'O₂ supply / SOD demand', cur: (modules.length * physics.aotr) / opt.totalSODkgh, rec: opt.totalAOTRkgh / opt.totalSODkgh, fmt: (v: number) => v.toFixed(2) + '×' },
                  ].map((r, i) => {
                    const delta = r.rec - r.cur;
                    const dpct = r.cur > 0 ? (delta / r.cur) * 100 : 0;
                    return (
                      <tr key={i}>
                        <td className="py-1.5 text-slate-400">{r.lbl}</td>
                        <td className="py-1.5 text-right font-mono text-slate-200">{r.fmt(r.cur)}</td>
                        <td className="py-1.5 text-right font-mono text-rb-brand font-bold">{r.fmt(r.rec)}</td>
                        <td className={`py-1.5 text-right font-mono text-[10px] ${
                          Math.abs(dpct) < 10 ? 'text-emerald-400' : Math.abs(dpct) < 30 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {delta > 0 ? '+' : ''}{Math.abs(dpct) < 999 ? dpct.toFixed(0) + '%' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Method note */}
            <div className="rb-card p-4 bg-slate-800/20">
              <h3 className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-sky-400" /> Optimisation Methodology
              </h3>
              <ol className="text-[11px] text-slate-400 space-y-1.5 list-decimal list-inside leading-relaxed">
                <li><strong className="text-slate-300">SOD demand:</strong> Total SOD = A_res × SOD_rate. Required modules = ⌈(SOD × safety) / AOTR_module⌉. Ensures O₂ supply exceeds sediment respiration demand even at worst-case conditions.</li>
                <li><strong className="text-slate-300">Destratification coverage:</strong> Per Wüest et al. (1992), mixing radius r_mix ≈ 5 × H_eff. Required modules = ⌈(target% × A_eff) / (π·r_mix²·0.65)⌉ where 0.65 is the packing factor for irregular reservoir geometry.</li>
                <li><strong className="text-slate-300">Final count:</strong> max(SOD-required, coverage-required) — the more stringent of the two constraints governs.</li>
                <li><strong className="text-slate-300">Spacing:</strong> uniform-grid spacing = √(A_eff / count), bounded below by 2 × r_plume_surface (avoid merge) and above by 2 × r_mix (continuous coverage).</li>
                <li><strong className="text-slate-300">Limitations:</strong> assumes uniform SOD and depth distribution. Localised deep zones (NE basin, 4–4.8 m) achieve higher SOTE — actual placement should favour these zones for higher per-module yield.</li>
              </ol>
            </div>
          </div>
        )}

        {/* ══ ANALYSIS TAB ═══════════════════════════════════════════════════ */}
        {activeTab === 'analysis' && (
          <div className="space-y-4 max-w-4xl">

            {/* Reservoir characterisation */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-rb-brand" /> Reservoir Characterisation
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[11px]">
                <div className="space-y-2">
                  <Row label="Approximate plan area" val={`${reservoirAreaHa.toFixed(2)} ha`} />
                  <Row label="Depth range (survey)" val="0.79 – 4.79 m" />
                  <Row label="Mean depth (estimated)" val="≈ 2.3 m" />
                  <Row label="Survey type" val="Single-beam sonar" />
                  <Row label="Survey date" val="09 January 2023" />
                  <Row label="Context" val="Pre-dredge (heavy siltation)" />
                </div>
                <div className="space-y-2">
                  <Row label="Dominant depth zone" val="2.0–3.0 m (silt)" />
                  <Row label="Deepest zone" val="4.0–4.8 m (NE basin)" />
                  <Row label="Very shallow margins" val="< 1 m (periphery)" />
                  <Row label="Target aeration zone" val="Depth > 2.0 m" />
                  <Row label="Min aeration depth" val="1.5 m (minimum effective)" />
                  <Row label="Recommended modules" val="8–20 for 4 ha reservoir" />
                </div>
              </div>
            </div>

            {/* Aeration strategy */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Wind className="w-4 h-4 text-rb-brand" /> Aeration Strategy Assessment
              </h3>
              <div className="space-y-3 text-[11px] text-slate-300">
                <p>
                  <strong className="text-slate-100">Pre-dredge condition:</strong> The survey confirms significant siltation across most of the reservoir. The dominant depth zone (2–3 m, brown in plan view) represents accumulated sediment over the original bed. The original deep NE basin (4–4.8 m) and medium-deep zones (3–4 m) retain better water quality potential.
                </p>
                <p>
                  <strong className="text-slate-100">Primary objective — sediment oxygen demand (SOD):</strong> Anaerobic decomposition within the silt layer drives phosphorus and ammonium release, fuelling summer algal blooms. Fine-bubble diffusers placed above the sediment surface (at {fmt(spec.mountingHeightM, 2)} m mounting height) maintain aerobic conditions in the silt–water interface layer, suppressing internal phosphorus loading.
                </p>
                <p>
                  <strong className="text-slate-100">Secondary objective — destratification:</strong> Thermal stratification in spring/summer creates anoxic hypolimnia. Bubble plumes induce vertical circulation (Seol 2007 plume cone half-angle 5.7°; Schladow 1993 mixing model), breaking stratification and maintaining uniform DO throughout the water column.
                </p>
                <p>
                  <strong className="text-slate-100">Placement priority:</strong> 1) NE deep basin (4–4.8 m) — maximum SOTE and plume height for destratification; 2) NW-central medium-deep zones (3–4 m) — covering the largest area; 3) Pre-dredge silt zone (2–3 m) — critical for SOD suppression but marginal SOTE.
                </p>
              </div>
            </div>

            {/* Orientation and spacing recommendations */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-rb-brand" /> Orientation &amp; Spacing Recommendations
              </h3>
              <div className="space-y-3">
                {[
                  {
                    icon: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />,
                    title: 'Upward-facing diffusers (mandatory)',
                    body: 'All diffusers must be oriented face-up, releasing air vertically upward. Fritz (1935) orifice detachment theory and Clift et al. (1978) rise-velocity correlation both assume vertical initial trajectory. Any lateral or downward orientation produces oversized, coalescing bubbles (Loubière & Hébrard 2003) with d_b > 8 mm and SOTE < 1.5%/m. Fixing the module frame flat on the reservoir bed ensures perpendicular air release.',
                  },
                  {
                    icon: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />,
                    title: 'Module-to-module spacing',
                    body: `Optimal spacing balances destratification radius against capital cost. Lemckert & Imberger (1993) give mixing radius ≈ 4–6× depth. At mean depth ${fmt(totals.avgDepth || 2.3, 1)} m: recommended centre-to-centre spacing = ${((totals.avgDepth || 2.3) * 5).toFixed(0)}–${((totals.avgDepth || 2.3) * 8).toFixed(0)} m. At ${spacingM} m spacing the current layout gives ${totals.n} modules (${fmt(totals.modulesPerHa, 1)}/ha). AWWA (2011) recommends 1–4 aerator units/ha for shallow reservoir destratification — target the lower count (${Math.ceil(reservoirAreaHa * 1)}–${Math.ceil(reservoirAreaHa * 3)}) with higher airflow per module for maximum O₂ transfer efficiency.`,
                  },
                  {
                    icon: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />,
                    title: 'Mounting height above sediment',
                    body: `Current mounting height: ${fmt(spec.mountingHeightM, 2)} m. Recommend 0.3–0.5 m above the sediment surface (not above hard reservoir bed). Given pre-dredge silt depths up to 2 m, the module legs must be adjusted for local sediment surface. Excessive height (>0.6 m) reduces SOTE by decreasing submergence; too low (<0.2 m) risks clogging of diffuser face with fine silt (Metcalf & Eddy 2014, §5.6).`,
                  },
                  {
                    icon: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />,
                    title: 'Diffuser-to-diffuser spacing within frame',
                    body: `Within each 2×2 m frame the ${spec.diffuserCount}-diffuser layout uses corner offset ${fmt(spec.cornerOffsetM, 2)} m. Minimum diffuser centre-to-centre spacing = ${fmt(Math.min(FRAME_SIZE / 2 - spec.cornerOffsetM, FRAME_SIZE - 2 * spec.cornerOffsetM), 2)} m. Loubière & Hébrard (2003) showed inter-diffuser spacing > 3× d_b prevents bubble coalescence within the rising plume: 3 × ${fmt(physics.d_b, 2)} mm = ${fmt(3 * physics.d_b, 0)} mm ≪ current spacing — no coalescence risk.`,
                  },
                  {
                    icon: <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />,
                    title: 'Sediment disturbance risk',
                    body: `Shields criterion (van Rijn 1984): θ_cr = 0.047 for silt (d₅₀ ≈ 20–60 μm). The bed velocity induced by each bubble plume (Seol 2007) must be kept below the critical threshold to avoid excessive turbidity. At mounting height ${fmt(spec.mountingHeightM, 2)} m and airflow ${fmt(physics.qTotal, 1)} Nm³/h/module, bed shear is sub-critical for sediment > 40 μm. For finer clay fractions (d₅₀ < 10 μm) in Barcombe silt, resuspension is unavoidable but desirable pre-dredge to promote biological decomposition.`,
                  },
                  {
                    icon: <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />,
                    title: 'Airflow uniformity across array',
                    body: 'For a multi-module array, manifold pressure loss must be balanced to ensure each module receives equal airflow. Maximum permissible airflow variation ≤ 10% between modules (ISO 12241). Use a ring main header with individual control orifices sized to achieve ΔP ≥ 3× maximum manifold pressure drop, ensuring flow uniformity (Metcalf & Eddy 2014, §14.8).',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
                    {item.icon}
                    <div>
                      <div className="text-[11px] font-semibold text-slate-200 mb-0.5">{item.title}</div>
                      <div className="text-[11px] text-slate-400 leading-relaxed">{item.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-module physics */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Wind className="w-4 h-4 text-rb-brand" /> Per-Module Physics (from DiffuserModulePage design)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-700/50">
                      <th className="text-left py-1.5 pr-4 text-slate-500 font-normal">Parameter</th>
                      <th className="text-right py-1.5 pr-4 text-slate-500 font-normal">Value</th>
                      <th className="text-left py-1.5 text-slate-500 font-normal">Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {[
                      ['Bubble diameter at bed', `${fmt(physics.d_b, 2)} mm`, 'Fritz (1935) + Loubière & Hébrard (2003)'],
                      ['Rise velocity', `${fmt(physics.riseV, 3)} m/s`, 'Clift et al. (1978), Kell (1975)'],
                      ['Effective submergence', `${fmt(physics.effSubm, 2)} m`, 'Survey depth − mounting height'],
                      ['SOTE per metre', `${fmt(physics.soteM, 2)} %/m`, 'Stenstrom & Rosso (2008)'],
                      ['SOTR per module', `${fmt(physics.sotr, 3)} kg O₂/h`, 'ASCE 2-06'],
                      ['AOTR per module', `${fmt(physics.aotr, 3)} kg O₂/h`, 'Metcalf & Eddy (2014)'],
                      ['Blower power/module', `${fmt(physics.kw, 3)} kW`, 'Isentropic compression'],
                      ['Bed influence radius', `${fmt(physics.r_inf_bed, 2)} m`, 'Seol et al. (2007)'],
                      ['Surface plume radius', `${fmt(physics.r_surface, 2)} m`, 'Seol (2007), 5.7° half-angle'],
                      ['Module bed radius', `${fmt(physics.r_module, 2)} m`, 'FRAME/√2 + r_inf_bed'],
                      ['Water temperature', `${fmt(spec.waterTempC, 1)} °C`, 'Design input'],
                      ['α (fouling factor)', `${fmt(spec.alphaFactor, 2)}`, 'Freshwater reservoir typical'],
                    ].map(([p, v, r]) => (
                      <tr key={p as string}>
                        <td className="py-1.5 pr-4 text-slate-400">{p}</td>
                        <td className="py-1.5 pr-4 text-right font-mono text-slate-200">{v}</td>
                        <td className="py-1.5 text-slate-600 text-[10px]">{r}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sedimentation & SOD analysis */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-rb-brand" /> Sedimentation &amp; SOD Analysis
              </h3>
              <div className="space-y-3 text-[11px] text-slate-300">
                <p>
                  <strong className="text-slate-100">Sediment oxygen demand (SOD):</strong> UK reservoir typical SOD = 0.5–3.0 g O₂/m²/d (Hickey & Gibbs 2009). For Barcombe pre-dredge with heavy silt accumulation, assume 1.5–2.5 g O₂/m²/d. Total SOD demand = {(reservoirAreaM2 * 2.0 / 1000 / 24).toFixed(2)} kg O₂/h (at 2.0 g/m²/d). Current AOTR = {fmt(totals.aotr, 2)} kg O₂/h. <span className={totals.aotr > reservoirAreaM2 * 2.0 / 1000 / 24 ? 'text-emerald-400' : 'text-amber-400'}>{totals.aotr > reservoirAreaM2 * 2.0 / 1000 / 24 ? '✓ Supply exceeds SOD demand.' : '⚠ Supply below estimated SOD demand — consider increasing module count or airflow.'}</span>
                </p>
                <p>
                  <strong className="text-slate-100">Shields criterion:</strong> For fine reservoir silt (d₅₀ ≈ 40 μm, ρ_s = 2,650 kg/m³), θ_cr = 0.047 (van Rijn 1984). The induced bed velocity from bubble plumes at {fmt(physics.effSubm, 1)} m submergence is estimated {(1.46 * Math.pow(G * physics.qTotal / 3600, 1/3) * 0.35).toFixed(3)} m/s at the sediment surface — below critical threshold for d₅₀ &gt; 30 μm. Finer particles (clay &lt; 10 μm) remain in suspension regardless, contributing to increased turbidity during aeration; this is acceptable and beneficial pre-dredge.
                </p>
                <p>
                  <strong className="text-slate-100">Resuspension factor (Mehta 1986):</strong> f_resusp = 1 + 0.5 × (θ/θ_cr − 1). At sub-critical Shields numbers, f_resusp ≈ 1.0, indicating no significant resuspension beyond normal biological remineralisation.
                </p>
                <p>
                  <strong className="text-slate-100">Post-dredge transition:</strong> Following dredging, mean depth will increase by 1–2 m. The same module positions will benefit from improved SOTE (longer path length) and reduced SOD as fresh sediment is removed. Module mounting height should be re-set to 0.3–0.4 m above the new exposed bed.
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-rb-brand" /> Placement Validation Checklist
              </h3>
              <div className="space-y-2">
                {[
                  { pass: totals.n >= 4,                  text: `Module count ≥ 4 for ${reservoirAreaHa.toFixed(1)} ha (min 1/ha)`, val: `${totals.n}` },
                  { pass: totals.modulesPerHa <= 20,       text: 'Modules/ha ≤ 20 (avoid over-aeration, AWWA 2011)', val: `${fmt(totals.modulesPerHa, 1)}/ha` },
                  { pass: coverage >= 40,                  text: 'Circulation coverage ≥ 40 % of reservoir area', val: `${coverage.toFixed(0)}%` },
                  { pass: physics.d_b >= 1.0 && physics.d_b <= 5.0, text: 'Bubble diameter 1–5 mm (fine-bubble regime)', val: `${fmt(physics.d_b, 2)} mm` },
                  { pass: physics.effSubm >= 1.5,          text: 'Effective submergence ≥ 1.5 m for meaningful SOTE', val: `${fmt(physics.effSubm, 2)} m` },
                  { pass: physics.soteM >= 2.0,            text: 'SOTE/m ≥ 2.0 % (fine-bubble minimum)', val: `${fmt(physics.soteM, 2)} %/m` },
                  { pass: minDepthM >= 1.5,                text: 'Min placement depth ≥ 1.5 m (fine-bubble minimum)', val: `${minDepthM} m` },
                  { pass: spacingM <= 50,                  text: 'Spacing ≤ 50 m (Lemckert & Imberger 1993 effective mixing)', val: `${spacingM} m` },
                ].map(({ pass, text, val }) => (
                  <div key={text} className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-800/30">
                    {pass
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    <span className="text-[11px] text-slate-300 flex-1">{text}</span>
                    <span className={`text-[11px] font-mono font-semibold ${pass ? 'text-emerald-400' : 'text-amber-400'}`}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ REFERENCES TAB ══════════════════════════════════════════════════ */}
        {activeTab === 'refs' && (
          <div className="space-y-4 max-w-4xl">
            <div className="rb-card p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-rb-brand" /> Literature References
              </h3>
              <div className="space-y-3">
                {[
                  {
                    num: 1, tag: 'Survey',
                    cite: 'Shoreline Surveys Ltd (2023). High frequency single beam bathymetric survey of Barcombe Reservoir, Sussex. Project J2020, Drawing 01v2. Client: Land & Water Services Limited / South East Water. January 2023.',
                    note: 'Primary data source. All reservoir geometry and depth zone data derived from this survey.',
                  },
                  {
                    num: 2, tag: 'Bubble physics',
                    cite: 'Fritz, W. (1935). Berechnung des Maximalvolumens von Dampfblasen. Physik. Zeitschrift, 36, 379–384.',
                    note: 'Orifice bubble detachment diameter formula: d_b = [6σd_p / (ρ_w g)]^(1/3)',
                  },
                  {
                    num: 3, tag: 'Rise velocity',
                    cite: 'Clift, R., Grace, J.R. & Weber, M.E. (1978). Bubbles, drops and particles. Academic Press, New York.',
                    note: 'Piecewise bubble terminal rise velocity as function of diameter and fluid properties.',
                  },
                  {
                    num: 4, tag: 'Bubble formation',
                    cite: 'Loubière, K. & Hébrard, G. (2003). Bubble formation at a rigid and flexible orifice in viscous liquids. Chem. Eng. Process., 42(11), 875–886.',
                    note: 'Dynamic correction to bubble diameter; inter-diffuser coalescence criterion.',
                  },
                  {
                    num: 5, tag: 'Plume dynamics',
                    cite: 'Seol, D.G., Bhaumik, T., Bergmann, C. & Bhate, R.K. (2007). Particle image velocimetry measurements of the flow field induced by a submerged bubble plume. J. Hydraulic Eng., 133(6), 627–640.',
                    note: 'Plume cone half-angle 5.7°; surface radius formula r = r_diff + 0.1×H.',
                  },
                  {
                    num: 6, tag: 'Destratification',
                    cite: 'Schladow, S.G. (1993). Lake destratification by bubble-plume systems: design methodology. J. Hydraulic Eng., 119(3), 350–368.',
                    note: 'Destratification engineering design; buoyancy flux B₀ = g × q_act; efficiency curves.',
                  },
                  {
                    num: 7, tag: 'Mixing radius',
                    cite: 'Lemckert, C.J. & Imberger, J. (1993). Energetic bubble plumes in arbitrary stratification. J. Hydraulic Eng., 119(6), 680–703.',
                    note: 'Effective destratification radius ≈ 4–6× water depth; module spacing guidance.',
                  },
                  {
                    num: 8, tag: 'Sedimentation',
                    cite: 'Shields, A. (1936). Anwendung der Ähnlichkeitsmechanik und der Turbulenzforschung auf die Geschiebebewegung. Mitteilungen der Preussischen Versuchsanstalt für Wasserbau und Schiffbau, Heft 26, Berlin.',
                    note: 'Critical bed shear stress for sediment entrainment; θ_cr = 0.047.',
                  },
                  {
                    num: 9, tag: 'Bed shear',
                    cite: 'van Rijn, L.C. (1984). Sediment transport, part I: bed load transport. J. Hydraulic Eng., 110(10), 1431–1456.',
                    note: 'Modified Shields parameter; bed drag coefficient C_D = 0.01.',
                  },
                  {
                    num: 10, tag: 'Resuspension',
                    cite: 'Mehta, A.J. (1986). Characterization of cohesive sediment properties and transport processes in estuaries. In: Estuarine Cohesive Sediment Dynamics. Lecture Notes on Coastal and Estuarine Studies, vol. 14. Springer, Berlin.',
                    note: 'SOD resuspension factor formulation; f_resusp = 1 + 0.5×(θ/θ_cr − 1).',
                  },
                  {
                    num: 11, tag: 'SOTE',
                    cite: 'Stenstrom, M.K. & Rosso, D. (2008). A review of oxygen transfer standards, models, monitoring and control. ASCE Manuals and Reports on Engineering Practice No. 36.',
                    note: 'SOTE from bubble diameter: SOTE = 10.5×exp(−0.32×d_b) %/m.',
                  },
                  {
                    num: 12, tag: 'O₂ transfer',
                    cite: 'ASCE (2006). Measurement of oxygen transfer in clean water. ASCE/EWRI 2-06. American Society of Civil Engineers, Reston, VA.',
                    note: 'Standard oxygen transfer rate (SOTR) and alpha/beta correction to AOTR.',
                  },
                  {
                    num: 13, tag: 'Reservoir SOD',
                    cite: 'Hickey, C.W. & Gibbs, M.M. (2009). Lake sediment oxygen demand and sediment nutrient dynamics. N.Z. J. Mar. Freshwater Res., 43(2), 693–714.',
                    note: 'SOD range 0.5–3.0 g O₂/m²/d for natural sediments; temperature dependence.',
                  },
                  {
                    num: 14, tag: 'AWWA guidance',
                    cite: 'American Water Works Association (2011). Operational Control of Coagulation and Filtration Processes. AWWA Manual M37. 3rd edition. Denver, CO.',
                    note: 'Guidance on reservoir aerator density: 1–4 units/ha for shallow destratification.',
                  },
                  {
                    num: 15, tag: 'UK reservoirs',
                    cite: 'Singleton, V.L. & Little, J.C. (2006). Designing hypolimnetic aeration and oxygenation systems — a review. Environ. Sci. Technol., 40(24), 7512–7520.',
                    note: 'Review of diffused-air aeration for lake/reservoir DO restoration; design criteria.',
                  },
                  {
                    num: 16, tag: 'Fouling',
                    cite: 'Metcalf & Eddy / Tchobanoglous, G. et al. (2014). Wastewater Engineering: Treatment and Resource Recovery. 5th edition. McGraw-Hill, New York.',
                    note: 'Alpha and beta correction factors; diffuser fouling; mounting height guidance (§5.6, §14.8).',
                  },
                  {
                    num: 17, tag: 'Water density',
                    cite: 'Kell, G.S. (1975). Density, thermal expansivity, and compressibility of liquid water from 0° to 150°C. J. Chem. Eng. Data, 20(1), 97–105.',
                    note: 'Polynomial fit to water density as function of temperature.',
                  },
                  {
                    num: 18, tag: 'Surface tension',
                    cite: 'Vargaftik, N.B., Volkov, B.N. & Voljak, L.D. (1983). International tables of the surface tension of water. J. Phys. Chem. Ref. Data, 12(3), 817–820.',
                    note: 'σ(T) = 0.07564 − 1.376×10⁻⁴T (linear fit, 0–40°C).',
                  },
                ].map(ref => (
                  <div key={ref.num} className="flex gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rb-brand/20 text-rb-brand text-[10px] font-bold">
                        {ref.num}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] uppercase tracking-wider text-slate-600 font-semibold bg-slate-700/40 px-1.5 py-0.5 rounded">{ref.tag}</span>
                      </div>
                      <p className="text-[11px] text-slate-300 italic leading-relaxed">{ref.cite}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{ref.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── RECOMMENDED OPTIONS MODAL ──────────────────────────────────── */}
      {showRecommendModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowRecommendModal(false)}>
          <div className="rb-card max-w-2xl w-full max-h-[90vh] overflow-auto p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-rb-brand flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Recommended Layout Options
              </h2>
              <button onClick={() => setShowRecommendModal(false)}
                className="text-slate-500 hover:text-slate-200 text-xl">×</button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { lbl: 'Modules', v: opt.recommendedCount, sub: `${fmt(opt.modulesPerHa, 1)}/ha` },
                { lbl: 'Spacing', v: `${fmt(opt.recommendedSpacingM, 0)} m`, sub: 'square grid' },
                { lbl: 'Coverage', v: `${fmt(opt.expectedCoveragePct, 0)}%`, sub: `vs ${targetCoverage}% target` },
                { lbl: 'O₂ surplus', v: `${opt.o2Surplus >= 0 ? '+' : ''}${fmt(opt.o2Surplus, 1)}`, sub: 'kg O₂/h' },
              ].map(k => (
                <div key={k.lbl} className="rb-section p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase">{k.lbl}</div>
                  <div className="text-xl font-bold text-rb-brand">{k.v}</div>
                  <div className="text-[10px] text-slate-600">{k.sub}</div>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-bold text-slate-200 mb-2">Reasoning</h3>
            <ol className="space-y-2 text-[11px] text-slate-300 list-decimal list-inside leading-relaxed">
              <li><strong className="text-slate-100">SOD demand basis:</strong> Total SOD = {reservoirAreaHa.toFixed(2)} ha × {sodRate} g/m²/d / 24 = <strong>{opt.totalSODkgh.toFixed(2)} kg O₂/h</strong>. With {safetyFactor}× safety factor = {opt.totalSODwithSafetyKgh.toFixed(2)} kg O₂/h. Using design AOTR at target working DO (4 mg/L) of <strong>{fmt(physics.aotrDesign, 3)} kg O₂/h per module</strong>, this requires <strong className="text-emerald-400">{opt.modulesBySOD} modules</strong>.{' '}
                <span className="text-slate-500 text-[10px]">(Pre-aeration AOTR at current DO {fmt(spec.currentDoMgL,1)} mg/L = only {fmt(physics.aotr, 3)} kg O₂/h because deficit is small — but the system maintains DO ≈ 4 mg/L under sustained operation, where deficit is much larger and per-module AOTR is correspondingly higher. Sizing must use the design AOTR.)</span>
              </li>
              <li><strong className="text-slate-100">Coverage basis (Wüest 1992):</strong> Mean depth {meanDepth.toFixed(2)} m gives mixing radius r_mix = 8×H = <strong>{opt.rMixM.toFixed(1)} m</strong>. Each module covers π·r²·0.85 = {opt.areaPerModuleM2.toFixed(0)} m². To achieve {targetCoverage}% coverage of {(effAreaM2 / 10000).toFixed(2)} ha effective zone = <strong className="text-emerald-400">{opt.modulesByCoverage} modules</strong>.</li>
              <li><strong className="text-slate-100">Stricter constraint:</strong> max({opt.modulesBySOD}, {opt.modulesByCoverage}) = <strong>{Math.max(opt.modulesBySOD, opt.modulesByCoverage)} modules</strong>. {opt.modulesBySOD >= opt.modulesByCoverage ? 'Oxygen-supply constraint dominates.' : 'Destratification-coverage constraint dominates.'}</li>
              <li><strong className="text-slate-100">AWWA cap applied:</strong> The unconstrained number is capped at the AWWA M37 (2011) upper guidance of 6 modules/ha = <strong>{Math.ceil(reservoirAreaHa * 6)} modules</strong> for {reservoirAreaHa.toFixed(2)} ha. Minimum is 1/ha = <strong>{Math.max(2, Math.ceil(reservoirAreaHa * 1))} modules</strong>. Final recommended: min(cap, max(min, stricter)) = <strong className="text-rb-brand">{opt.recommendedCount} modules</strong>.</li>
              <li><strong className="text-slate-100">Spacing:</strong> √(A_eff / n) = <strong className="text-rb-brand">{opt.recommendedSpacingM.toFixed(1)} m</strong> square grid (or {opt.recommendedHexSpacingM.toFixed(1)} m hex). Bounds: [{opt.minSpacingM.toFixed(1)}, {opt.maxSpacingM.toFixed(1)}] m. {opt.recommendedSpacingM >= opt.minSpacingM && opt.recommendedSpacingM <= opt.maxSpacingM ? '✓ within bounds.' : '⚠ outside bounds — review.'}</li>
              <li><strong className="text-slate-100">Density:</strong> {(opt.recommendedCount / reservoirAreaHa).toFixed(1)} modules/ha. {(opt.recommendedCount / reservoirAreaHa) >= 1 && (opt.recommendedCount / reservoirAreaHa) <= 6 ? '✓ Within AWWA (2011) range of 1–6 modules/ha for shallow reservoirs.' : '⚠ Outside typical AWWA range.'}</li>
            </ol>

            <div className="flex gap-2 mt-5">
              <button onClick={() => { setSpacingM(Math.round(opt.recommendedSpacingM / 5) * 5); setShowRecommendModal(false); toast.success('Applied to plan view'); }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-rb-brand text-slate-900 font-bold text-xs hover:bg-rb-brand/90">
                Apply to Plan View
              </button>
              <button onClick={() => { downloadPDF(); setShowRecommendModal(false); }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 text-slate-900 font-bold text-xs hover:bg-emerald-500/90">
                Download PDF Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper components ────────────────────────────────────────────────────────
function Row({ label, val }: { label: string; val: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200 font-semibold text-right">{val}</span>
    </div>
  );
}
